import * as React from 'react';
import {
  AddTile,
  Alert,
  BottomSheet,
  BottomSheetContent,
  Button,
  CameraFeed,
  canOpenCamera,
  captureFrame,
  Stack,
  VinConfirm,
  VinScanProcessing,
  type CameraFeedState,
} from '@relay-towing/ui';
import { useScanVin, type ScannedVin } from '../../hooks/useScanVin';
import { capturePhoto, isNative, pickMedia } from '../../lib/nativeBridge';
import { cameraNotice } from './CameraNotice';

type Step = 'camera' | 'reading' | 'confirm';

/** Each step's title, in the Figma's words. */
const TITLES: Record<Step, string> = {
  camera: 'Enter VIN',
  reading: 'Processing VIN',
  confirm: 'Processing VIN',
};

/**
 * Reading a VIN off a picture, as the Figma's Add Vehicle - VIN row draws
 * it: the Enter VIN sheet (460:83400) with the camera in it, Add VIN
 * Manually under it, and Upload image beside Capture; then Processing VIN
 * while the backend reads and decodes the picture, and the VIN it found to
 * confirm (460:83744). Confirm hands the VIN and what it decodes to back;
 * Enter manually, Tap to edit and Add VIN Manually go to the vehicle
 * sheet's own VIN box, with the VIN filled in when there is one, for a
 * single wrong character.
 *
 * In a browser that can open a camera the picture is the live camera's
 * frame when Capture is tapped. In a wrapper it is the phone's own camera,
 * which the bridge has as a photo and not as a feed, so the sheet shows no
 * camera and Capture opens it; anywhere else, the browser's file input.
 * The backend reads a picture per request, so the feed is never sent frame
 * by frame: only the one Capture takes.
 *
 * The Figma's Processing frame (460:83560) spells the VIN out as it is
 * read, with Continue to be told later; the reader answers once, with the
 * whole VIN, so the wait is a spinner and the VIN comes with Confirm.
 */
export function VinScanSheet({
  open,
  onOpenChange,
  onRead,
  onEnterManually,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRead: (scanned: ScannedVin) => void;
  /** To typing: with the VIN the camera found, when it found one. */
  onEnterManually: (vin?: string) => void;
}) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <VinScan onRead={onRead} onEnterManually={onEnterManually} />
    </BottomSheet>
  );
}

/**
 * The sheet's steps. Its own component so each opening starts at the
 * camera: the sheet's content is unmounted while it is shut, and with it
 * the camera, so the phone's camera light goes out.
 */
function VinScan({
  onRead,
  onEnterManually,
}: {
  onRead: (scanned: ScannedVin) => void;
  onEnterManually: (vin?: string) => void;
}) {
  const { scan } = useScanVin();
  const video = React.useRef<HTMLVideoElement>(null);
  const live = !isNative() && canOpenCamera();
  const [step, setStep] = React.useState<Step>('camera');
  const [camera, setCamera] = React.useState<CameraFeedState>('starting');
  const [found, setFound] = React.useState<ScannedVin | null>(null);
  const [failed, setFailed] = React.useState<string | null>(null);

  const read = async (picture: File) => {
    setFailed(null);
    setStep('reading');
    try {
      const result = await scan(picture);
      if (result) {
        setFound(result);
        setStep('confirm');
        return;
      }
      setFailed('No VIN could be read from that picture. Try another.');
    } catch {
      setFailed('The picture could not be read. Try again.');
    }
    setStep('camera');
  };

  const first = async (picked: Promise<{ files: File[] }>) => {
    const { files } = await picked;
    if (files[0]) await read(files[0]);
  };

  const capture = async () => {
    if (!live) return first(capturePhoto());
    if (!video.current) return;
    const picture = await captureFrame(video.current, 'vin.jpg');
    if (picture) await read(picture);
  };

  return (
    <BottomSheetContent
      title={TITLES[step]}
      size={step === 'camera' ? 'full' : 'auto'}
      footer={
        step === 'camera' ? (
          <>
            <Button
              variant="secondary"
              size="xl"
              pill
              onClick={() => first(pickMedia({ allowVideo: false }))}
            >
              Upload image
            </Button>
            <Button
              size="xl"
              pill
              disabled={live && camera !== 'live'}
              onClick={capture}
            >
              Capture
            </Button>
          </>
        ) : undefined
      }
    >
      {step === 'camera' ? (
        <Stack gap={4}>
          {failed ? <Alert tone="destructive">{failed}</Alert> : null}
          {live ? cameraNotice(camera) : null}
          {/* Hidden under a notice rather than unmounted: unmounting lets
              go of the camera, and mounting again would ask for it again. */}
          {live ? (
            <CameraFeed
              ref={video}
              framed
              hidden={!!cameraNotice(camera)}
              onStateChange={setCamera}
            />
          ) : null}
          <AddTile width="block" compact onClick={() => onEnterManually()}>
            Add VIN Manually
          </AddTile>
        </Stack>
      ) : null}
      {step === 'reading' ? <VinScanProcessing /> : null}
      {step === 'confirm' && found ? (
        <VinConfirm
          vin={found.vin}
          vehicle={
            found.vehicle
              ? {
                  make: found.vehicle.make,
                  model: found.vehicle.model,
                  year: found.vehicle.year,
                }
              : undefined
          }
          onConfirm={() => onRead(found)}
          onEnterManually={() => onEnterManually(found.vin)}
        />
      ) : null}
    </BottomSheetContent>
  );
}
